import {
  Drawer as DrawerRoot,
  DrawerContent as DrawerContentPrimitive,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
} from "./vaul-drawer";
import { XIcon } from "lucide-react";

import { cn } from "@/utils/cn";
import { DialogClose } from "@radix-ui/react-dialog";

type DrawerStyles = {
  header?: string;
  body?: string;
  footer?: string;
  drawerContent?: string;
  closeButton?: string;
};

interface DrawerProps {
  isOpen: boolean;
  title?: string;
  /**
   * Accessible dialog name for drawers that render their own header instead of
   * passing `title` (which would draw the built-in one). Without either, the
   * dialog has no accessible name and screen readers announce just "dialog".
   */
  srTitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose?: () => void;
  styles?: DrawerStyles;
  blurOwnContent?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  isDismissable?: boolean;
  placement?: "left" | "right" | "top" | "bottom";
  btnContent?: React.ReactNode;
  showBtn?: boolean;
  hideCloseButton?: boolean;
}

// Map size prop to width classes
const getSizeClass = (size?: string) => {
  switch (size) {
    case "sm":
      return "sm:max-w-sm!";
    case "md":
      return "sm:max-w-md!";
    case "lg":
      return "sm:max-w-lg!";
    case "xl":
      return "sm:max-w-xl!";
    case "2xl":
      return "sm:max-w-2xl!";
    case "3xl":
      return "sm:max-w-3xl!";
    case "4xl":
      return "sm:max-w-4xl!";
    case "5xl":
      return "sm:max-w-5xl!";
    case "6xl":
      return "sm:max-w-6xl!";
    default:
      return "";
  }
};

export const Drawer = ({
  isOpen,
  title = "",
  srTitle,
  children,
  footer,
  onClose,
  placement = "right",
  styles,
  blurOwnContent = false,
  size,
  isDismissable = true,
  btnContent,
  showBtn,
  hideCloseButton = false,
}: DrawerProps) => {
  return (
    <DrawerRoot
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
      direction={placement}
      dismissible={isDismissable}
    >
      <DrawerTitle className="sr-only">{title || srTitle}</DrawerTitle>
      <DrawerContentPrimitive
        aria-describedby={undefined}
        // `[will-change:auto]!` overrides vaul's persistent `will-change:transform`,
        // which otherwise keeps the panel on a GPU layer at a fractional device
        // pixel → intermittently blurry text once the drawer settles.
        className={cn(
          "bg-white rounded-sm w-full! max-w-[95%] [will-change:auto]!",
          getSizeClass(size),
          styles?.drawerContent,
        )}
      >
        {title && (
          <DrawerHeader
            className={cn(
              "text-[18px] gap-3 flex-row font-medium flex justify-between items-center",
              styles?.header,
            )}
          >
            <div className="flex flex-col md:flex-row justify-between grow sm:w-full items-start md:items-center gap-2 min-w-0">
              <span className="text-nowrap text-xs md:text-base text-muted-foreground">
                {title}
              </span>
              {showBtn && btnContent}
            </div>
            {!hideCloseButton && (
              <DialogClose
                onClick={onClose}
                className={cn(
                  "shrink-0 flex items-center justify-center rounded-md w-7 h-7 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors",
                  styles?.closeButton,
                )}
              >
                <XIcon className="size-4" />
              </DialogClose>
            )}
          </DrawerHeader>
        )}
        <div className={cn("p-4 overflow-y-auto scrollbar-thin", styles?.body)}>
          {children}
          <div id="radix-portal-root" />
        </div>
        {footer && (
          <DrawerFooter
            className={cn(
              "p-4 relative rounded-b-sm",
              // if backdrop blur (default): translucent bg + utility class
              !blurOwnContent ? "bg-white/30 backdrop-blur-md" : "", // if blurOwnContent, no special bg
              styles?.footer,
            )}
            style={
              blurOwnContent
                ? { filter: "blur(4px)" }
                : {
                    backdropFilter: "blur(10px)", // Chrome+modern
                    WebkitBackdropFilter: "blur(10px)", // Safari
                  }
            }
          >
            {footer}
          </DrawerFooter>
        )}
      </DrawerContentPrimitive>
    </DrawerRoot>
  );
};
